import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldHelp, HelpLabel } from '@/features/guided-help';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CreditCard,
  Wallet,
  AlertTriangle,
  Info,
  Calculator,
} from 'lucide-react';
import { PaymentType } from '@/types/tournament.model';
import type { Price } from '@/types';
import { useFixedFeeAmount } from '@/hooks/useFixedFeeAmount';
import { mercadoPagoService } from '@/api/mercadopago/mercadoPagoApi';
import { cn } from '@/lib/utils';
import {
  DEFAULT_PRICING_STEP_CONFIG,
  type PricingStepConfig,
} from './types';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export interface PricingCalculatorFieldProps {
  price: Price;
  onPriceChange: (price: Price) => void;
  paymentType: PaymentType;
  onPaymentTypeChange: (paymentType: PaymentType) => void;
  onPriceValidationChange?: (isValid: boolean) => void;
  config?: PricingStepConfig;
  /** When false, renders the calculator content without the outer Card (e.g. inside a modal). */
  wrapInCard?: boolean;
}

export function PricingCalculatorField({
  price,
  onPriceChange,
  paymentType,
  onPaymentTypeChange,
  onPriceValidationChange,
  config: userConfig,
  wrapInCard = true,
}: PricingCalculatorFieldProps) {
  const config = { ...DEFAULT_PRICING_STEP_CONFIG, ...userConfig };
  const { fixedFeeAmount, isLoading: isFeeLoading } = useFixedFeeAmount();

  const [mercadoPagoFee, setMercadoPagoFee] = useState({
    days: 0,
    commission: 0,
  });
  const [isMpFeeLoading, setIsMpFeeLoading] = useState(true);
  const [priceError, setPriceError] = useState<string>('');
  const onPriceValidationChangeRef = useRef(onPriceValidationChange);
  onPriceValidationChangeRef.current = onPriceValidationChange;

  useEffect(() => {
    const fetchMpFee = async () => {
      setIsMpFeeLoading(true);
      try {
        const feeInfo = await mercadoPagoService.getFeeInfo();
        setMercadoPagoFee(feeInfo);
      } catch (error) {
        console.error('Error fetching MercadoPago fee:', error);
      } finally {
        setIsMpFeeLoading(false);
      }
    };
    fetchMpFee();
  }, []);

  useEffect(() => {
    if (
      config.isBalanceRestricted &&
      paymentType !== PaymentType.MERCADOPAGO
    ) {
      onPaymentTypeChange(PaymentType.MERCADOPAGO);
    }
  }, [config.isBalanceRestricted, paymentType, onPaymentTypeChange]);

  useEffect(() => {
    if (fixedFeeAmount === null || fixedFeeAmount === undefined) return;

    const priceValue = price?.value ?? 0;
    const isPriceValid = priceValue > fixedFeeAmount;

    if (priceValue > 0 && !isPriceValid) {
      setPriceError(
        `El precio debe ser mayor a $${fixedFeeAmount.toLocaleString('es-AR')} para cubrir la comisión de GetMatch`,
      );
    } else if (priceValue === 0) {
      setPriceError('Debes ingresar un precio');
    } else {
      setPriceError('');
    }

    onPriceValidationChangeRef.current?.(isPriceValid);
  }, [price?.value, fixedFeeAmount]);

  const handlePriceChange = useCallback(
    (value: string) => {
      const numericValue = parseInt(value.replace(/\D/g, ''), 10) || 0;
      onPriceChange({
        id: price?.id ?? '',
        value: numericValue,
        currency:
          price?.currency ??
          config.currency ??
          DEFAULT_PRICING_STEP_CONFIG.currency,
      });
    },
    [price?.id, price?.currency, onPriceChange, config.currency],
  );

  const feeCalculations = useMemo(() => {
    const tournamentPrice = price?.value || 0;
    const fee = fixedFeeAmount ?? 0;
    const mpFeePercentage = mercadoPagoFee.commission / 100;
    const mpFeeWithVAT = mpFeePercentage * 1.21;
    const mpFeeAmount = tournamentPrice * mpFeeWithVAT;

    return {
      tournamentPrice,
      fixedFee: fee,
      mpFeePercentage: mercadoPagoFee.commission,
      mpFeeAmount,
      organizerIncome: {
        [PaymentType.ALL]: tournamentPrice - fee,
        [PaymentType.CASH]: tournamentPrice - fee,
        [PaymentType.MERCADOPAGO]: tournamentPrice - mpFeeAmount - fee,
      },
    };
  }, [price?.value, fixedFeeAmount, mercadoPagoFee.commission]);

  const isLoading = isFeeLoading || isMpFeeLoading;
  const selectedPaymentType = paymentType ?? PaymentType.MERCADOPAGO;
  const availablePaymentTypes = config.isBalanceRestricted
    ? [PaymentType.MERCADOPAGO]
    : (config.allowedPaymentTypes ?? [
        PaymentType.MERCADOPAGO,
        PaymentType.CASH,
        PaymentType.ALL,
      ]);

  const calculatorInner = (
    <>
      {config.isBalanceRestricted && (
        <Alert variant="destructive" className={wrapInCard ? undefined : 'mb-4'}>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Tu saldo deudor excede el límite permitido. Solo puedes crear
            torneos con pago vía Mercado Pago.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="space-y-3" data-guide-id="wizard.pricing.paymentType">
            <HelpLabel
              topicId="wizard.pricing.paymentType"
              className="text-base font-medium"
            >
              Método de pago
            </HelpLabel>
            <RadioGroup
              value={selectedPaymentType}
              onValueChange={(value) =>
                onPaymentTypeChange(value as PaymentType)
              }
              className="space-y-2"
            >
              {availablePaymentTypes.map((method) => (
                <div
                  key={method}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                    selectedPaymentType === method
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300',
                  )}
                >
                  <RadioGroupItem
                    value={method}
                    id={`payment-${method}`}
                    disabled={
                      config.isBalanceRestricted &&
                      method === PaymentType.ALL
                    }
                  />
                  <Label
                    htmlFor={`payment-${method}`}
                    className="flex flex-1 cursor-pointer items-center gap-2"
                  >
                    {method === PaymentType.MERCADOPAGO ? (
                      <>
                        <CreditCard className="h-4 w-4 text-blue-600" />
                        <span>Solo Mercado Pago</span>
                      </>
                    ) : method === PaymentType.CASH ? (
                      <>
                        <Wallet className="h-4 w-4 text-amber-600" />
                        <span>Solo efectivo / transferencia</span>
                      </>
                    ) : (
                      <>
                        <Wallet className="h-4 w-4 text-green-600" />
                        <span>Mercado Pago y efectivo / transferencia</span>
                      </>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2" data-guide-id="wizard.pricing.price">
            <HelpLabel
              htmlFor="price"
              topicId="wizard.pricing.price"
              className="text-base font-medium"
            >
              Precio del torneo por jugador
            </HelpLabel>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <Input
                id="price"
                type="text"
                value={(price?.value || 0).toLocaleString('es-AR')}
                onChange={(e) => handlePriceChange(e.target.value)}
                className={cn(
                  'pl-8 text-lg',
                  priceError &&
                    'border-orange-400 focus:border-orange-500 focus:ring-orange-500',
                )}
                placeholder="0"
              />
            </div>
            {priceError && (
              <p className="text-sm text-orange-600">{priceError}</p>
            )}
          </div>

          <div className="space-y-2 text-sm text-gray-600">
            <p>
              Se aplica una comisión fija de{' '}
              <strong>${fixedFeeAmount?.toLocaleString('es-AR')}</strong> en
              todos los casos.
            </p>
            {(selectedPaymentType === PaymentType.MERCADOPAGO ||
              selectedPaymentType === PaymentType.ALL) && (
              <p>
                Además, se aplica una comisión adicional del{' '}
                <strong>{mercadoPagoFee.commission}% + 21% de IVA</strong> para
                pagos con MercadoPago (liberación en {mercadoPagoFee.days}{' '}
                días).
              </p>
            )}
          </div>

          {(selectedPaymentType === PaymentType.ALL ||
            selectedPaymentType === PaymentType.CASH) && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                La comisión por efectivo se coloca como deuda en la cuenta
                corriente del organizador del torneo con GetMatch.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-4 font-semibold text-gray-800">
            Desglose por método de pago:
          </h3>

          <div className="space-y-4">
            {selectedPaymentType === PaymentType.ALL ? (
              <>
                <FeeBreakdownItem
                  title="Pago en efectivo/transferencia"
                  tournamentPrice={feeCalculations.tournamentPrice}
                  fixedFee={feeCalculations.fixedFee}
                  organizerIncome={
                    feeCalculations.organizerIncome[PaymentType.CASH]
                  }
                  hasPriceError={!!priceError}
                />
                <FeeBreakdownItem
                  title="Pago con Mercado Pago"
                  tournamentPrice={feeCalculations.tournamentPrice}
                  fixedFee={feeCalculations.fixedFee}
                  mpFeePercentage={feeCalculations.mpFeePercentage}
                  mpFeeAmount={feeCalculations.mpFeeAmount}
                  organizerIncome={
                    feeCalculations.organizerIncome[PaymentType.MERCADOPAGO]
                  }
                  hasPriceError={!!priceError}
                />
              </>
            ) : selectedPaymentType === PaymentType.CASH ? (
              <FeeBreakdownItem
                title="Pago en efectivo/transferencia"
                tournamentPrice={feeCalculations.tournamentPrice}
                fixedFee={feeCalculations.fixedFee}
                organizerIncome={
                  feeCalculations.organizerIncome[PaymentType.CASH]
                }
                hasPriceError={!!priceError}
              />
            ) : (
              <FeeBreakdownItem
                title="Pago con Mercado Pago"
                tournamentPrice={feeCalculations.tournamentPrice}
                fixedFee={feeCalculations.fixedFee}
                mpFeePercentage={feeCalculations.mpFeePercentage}
                mpFeeAmount={feeCalculations.mpFeeAmount}
                organizerIncome={
                  feeCalculations.organizerIncome[PaymentType.MERCADOPAGO]
                }
                hasPriceError={!!priceError}
              />
            )}
          </div>

          {(selectedPaymentType === PaymentType.ALL ||
            selectedPaymentType === PaymentType.CASH) && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                En caso de cancelaciones o no presentación de participantes 48
                horas antes del evento, la comisión por pagos en efectivo ya
                cobrada no será reintegrada a tu cuenta corriente.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </>
  );

  if (isLoading) {
    return (
      <Card className={cn('w-full', wizardBlockCardClassName)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculadora de Tarifas
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </CardContent>
      </Card>
    );
  }

  if (!wrapInCard) {
    return (
      <div className={cn(wizardBlockShellClassName, 'space-y-6')}>
        {calculatorInner}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className={wizardBlockCardClassName}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" />
            Calculadora de Tarifas
          </CardTitle>
        </CardHeader>
        <CardContent>{calculatorInner}</CardContent>
      </Card>
    </div>
  );
}

interface FeeBreakdownItemProps {
  title: string;
  tournamentPrice: number;
  fixedFee: number;
  mpFeePercentage?: number;
  mpFeeAmount?: number;
  organizerIncome: number;
  hasPriceError: boolean;
}

function FeeBreakdownItem({
  title,
  tournamentPrice,
  fixedFee,
  mpFeePercentage,
  mpFeeAmount,
  organizerIncome,
  hasPriceError,
}: FeeBreakdownItemProps) {
  const formatCurrency = (value: number) => {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-2" data-guide-id="wizard.pricing.fee">
      <div className="flex items-center gap-2">
        <h4 className="font-medium text-gray-700">{title}</h4>
        <FieldHelp topicId="wizard.pricing.fee" />
      </div>
      <div
        className={cn(
          'space-y-2 rounded-md p-3',
          hasPriceError ? 'border border-orange-200 bg-orange-50' : 'bg-white',
        )}
      >
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Precio del torneo:</span>
          <span className="font-medium">${formatCurrency(tournamentPrice)}</span>
        </div>

        {mpFeePercentage !== undefined && mpFeeAmount !== undefined && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">
              Comisión Mercado Pago ({mpFeePercentage}% + IVA):
            </span>
            <span className="font-medium text-red-600">
              -${formatCurrency(mpFeeAmount)}
            </span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Comisión GetMatch:</span>
          <span className="font-medium text-red-600">
            -${formatCurrency(fixedFee)}
          </span>
        </div>

        <div
          className={cn(
            'flex justify-between border-t pt-2 font-semibold',
            hasPriceError ? 'border-orange-200' : 'border-gray-200',
          )}
        >
          <span className="text-gray-800">Ingreso del organizador:</span>
          <span
            className={cn(
              'text-lg',
              hasPriceError
                ? 'text-orange-600'
                : organizerIncome >= 0
                  ? 'text-green-600'
                  : 'text-red-600',
            )}
          >
            ${formatCurrency(organizerIncome)}
          </span>
        </div>
      </div>
    </div>
  );
}
